import { Theme } from "@mui/material/styles";
import { IconButton, Tooltip } from "@mui/material";
import { AddPhotoAlternate, Clear, FileUpload, Pause, PlayArrow } from "@mui/icons-material";

import Box from "../Box";
import Button from "../Button";
import Typography from "../Typography";
import CircularProgress from "../CircularProgress";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import { Mode } from "../../constants/enums";

import { box, typography } from "./styles";

interface IProps {
    uploadProgress: number;
    originalFileId: string;
    uploading: boolean;
    mode: Mode;
    optimize?: boolean;
    pausing: boolean;
    title: string;
    description: string;
    imageInputId: string;
    imageInputRef: React.MutableRefObject<any>;
    handleRemove: () => void;
    handleTerminateUpload: () => void;
    handleContinueOrPauseUpload: () => void;
    uploadFile: (file: File) => Promise<void>;
}

const UploadBox = ({
    uploadProgress,
    originalFileId,
    uploading,
    mode,
    optimize,
    pausing,
    title,
    description,
    imageInputId,
    imageInputRef,
    handleRemove,
    handleTerminateUpload,
    handleContinueOrPauseUpload,
    uploadFile,
}: IProps) => {
    const Strings = useStrings();

    return (
        <>
            <Box sx={(theme: Theme) => box(theme, { height: "142px" })}>
                <Box
                    sx={(theme: Theme) =>
                        box(theme, {
                            gap: 1,
                            flexDirection: originalFileId || uploading ? "row" : "column",
                        })
                    }
                >
                    <UploadForm
                        uploadProgress={uploadProgress}
                        originalFileId={originalFileId}
                        uploading={uploading}
                        mode={mode}
                        pausing={pausing}
                        title={title}
                        description={description}
                        handleTerminateUpload={handleTerminateUpload}
                        handleContinueOrPauseUpload={handleContinueOrPauseUpload}
                    />
                </Box>
            </Box>

            {mode !== Mode.View && (
                <Box>
                    <ActionBox
                        originalFileId={originalFileId}
                        handleRemove={handleRemove}
                        imageInputId={imageInputId}
                        imageInputRef={imageInputRef}
                    />
                </Box>
            )}

            <input
                hidden
                ref={imageInputRef}
                id={imageInputId}
                type="file"
                accept="image/*"
                onChange={(event) => {
                    if (event.target.files?.[0]) {
                        uploadFile(event.target.files[0]);
                    }
                }}
            />
        </>
    );
};

const UploadForm = ({
    uploadProgress,
    originalFileId,
    uploading,
    mode,
    pausing,
    title,
    description,
    handleTerminateUpload,
    handleContinueOrPauseUpload,
}: {
    uploadProgress: number;
    originalFileId: string;
    uploading: boolean;
    mode: Mode;
    pausing: boolean;
    title: string;
    description: string;
    handleTerminateUpload: () => void;
    handleContinueOrPauseUpload: () => void;
}) => {
    const Strings = useStrings();

    if (originalFileId) return <Box component="img" src={Helpers.getFileAccessUrl(originalFileId)} />;
    if (uploading)
        return (
            <>
                <CircularProgress value={uploadProgress} size={65} />
                <div style={{ width: "25%" }}>
                    {mode !== Mode.View && (
                        <Tooltip arrow title={Strings.Common.CANCEL} aria-label={Strings.Common.CANCEL} placement="top">
                            <IconButton size="small" onClick={handleTerminateUpload}>
                                <Clear />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip
                        arrow
                        title={pausing ? Strings.Common.CONTINUE : Strings.Common.PAUSE}
                        aria-label={pausing ? Strings.Common.CONTINUE : Strings.Common.PAUSE}
                        placement="top"
                    >
                        <IconButton size="small" onClick={handleContinueOrPauseUpload}>
                            {pausing ? <PlayArrow /> : <Pause />}
                        </IconButton>
                    </Tooltip>
                </div>
            </>
        );
    return (
        <>
            <AddPhotoAlternate sx={{ fontSize: "3rem", color: "#d3d3d3" }} />
            <Typography sx={(theme: Theme) => typography(theme, { fontWeight: 500 })}>{title}</Typography>
            <Typography sx={(theme: Theme) => typography(theme)}>{description}</Typography>
        </>
    );
};

const ActionBox = ({
    originalFileId,
    handleRemove,
    imageInputId,
    imageInputRef,
}: {
    originalFileId: string;
    handleRemove: () => void;
    imageInputId: string;
    imageInputRef: React.MutableRefObject<any>;
}) => {
    const Strings = useStrings();

    if (originalFileId)
        return (
            <>
                <Button variant="text" onClick={handleRemove}>
                    {Strings.Common.REMOVE}
                </Button>

                <label id={imageInputId + "Label"} htmlFor={imageInputId}>
                    <Button variant="outlined" onClick={() => imageInputRef && imageInputRef.current.click()}>
                        {Strings.Common.REPLACE}
                    </Button>
                </label>
            </>
        );

    return (
        <label id={imageInputId + "Label"} htmlFor={imageInputId}>
            <Button variant="outlined" onClick={() => imageInputRef && imageInputRef.current.click()}>
                <FileUpload />
                {Strings.Common.UPLOAD}
            </Button>
        </label>
    );
};

export default UploadBox;
